/**
 * The pool screen's share control: one icon button, at the right of the top
 * row (ISSUE-952 R2).
 *
 * It resolves the invite link exactly the way the invite sheet's header does,
 * through `resolveInviteHeaderModel`, so a Commissioner can never be handed a
 * personal share link and a plain member can never be handed the pool's join
 * code. Both reads are issued unconditionally with the wrong one switched off
 * through `enabled`; never call a hook inside an `if`.
 *
 * The sentence it sends is `buildPoolShareMessage`, the one source of truth
 * the invite sheet's `mailto:` body also calls (R11).
 */
import { useJoinGrantGet, useShareLinkGet } from "@sportsmet/pool-sdk/private";
import { Button } from "@sportsmet/ui";
import type { MouseEvent, ReactNode } from "react";

import { extractErrorCode } from "../../errors/apiErrorBus";
import { ShareIcon } from "../../icons/actionIcons";
import { resolveInviteHeaderModel } from "../poolInvite/inviteHeaderModel";
import {
  buildInviteMailtoHref,
  buildInviteShareSubject,
} from "../poolInvite/inviteShareHref";
import { buildPoolShareMessage } from "../poolInvite/poolShareText";
import { POOL_SHARE_LABEL } from "./poolScreenCopy";

export type typePoolShareButtonProps = {
  poolId: string;
  poolName: string;
  viewerPoolRole: "owner" | "admin" | "member";
};

export const PoolShareButton = ({
  poolId,
  poolName,
  viewerPoolRole,
}: typePoolShareButtonProps): ReactNode => {
  const isCommissioner =
    viewerPoolRole === "owner" || viewerPoolRole === "admin";

  const grant = useJoinGrantGet({ poolId }, { enabled: isCommissioner });
  const shareLink = useShareLinkGet({ poolId }, { enabled: !isCommissioner });

  const model = resolveInviteHeaderModel({
    viewerPoolRole,
    grant: {
      isLoading: grant.isLoading,
      isTransportError: grant.isError,
      errorCode: extractErrorCode(grant.data),
      joinCode: grant.data?.data?.joinCode ?? null,
      joinUrl: grant.data?.data?.joinUrl ?? null,
    },
    shareLink: {
      isLoading: shareLink.isLoading,
      isTransportError: shareLink.isError,
      errorCode: extractErrorCode(shareLink.data),
      shareUrl: shareLink.data?.data?.shareUrl ?? null,
      active: shareLink.data?.data?.active ?? false,
    },
  });

  function handleShareClick(
    event: MouseEvent<HTMLAnchorElement>,
    inviteUrl: string,
  ): void {
    if (
      typeof navigator === "undefined" ||
      typeof navigator.share !== "function"
    ) {
      return;
    }
    event.preventDefault();
    void navigator
      .share({
        title: buildInviteShareSubject(poolName),
        text: buildPoolShareMessage({ poolName, inviteUrl }),
        url: inviteUrl,
      })
      .catch(() => {
        // A fan dismissing the OS share sheet rejects this promise. That is
        // not an error and never raises the error modal or a toast.
      });
  }

  // Sharing is switched off for this pool. There is nothing to share, so the
  // row carries no control rather than a button that cannot keep its promise.
  if (model.phase === "sharingOff") return null;

  if (model.phase === "loading" || model.phase === "failed") {
    return (
      <Button
        variant="ghost"
        size="icon"
        disabled
        aria-label={POOL_SHARE_LABEL}
        data-testid="pool-share-pending"
      >
        {/* The three node glyph: the owner rejected the legacy arrow out of a box on 2026-09-20 (ISSUE-952 R10). */}
        <ShareIcon testId="pool-share-icon" />
      </Button>
    );
  }

  const inviteUrl = model.inviteUrl;
  return (
    <Button asChild variant="ghost" size="icon">
      <a
        href={buildInviteMailtoHref({ poolName, inviteUrl })}
        aria-label={POOL_SHARE_LABEL}
        data-testid="pool-share"
        onClick={(event) => {
          handleShareClick(event, inviteUrl);
        }}
      >
        <ShareIcon testId="pool-share-icon" />
      </a>
    </Button>
  );
};
